import { TranscriptMessage } from "@/lib/transcript"
import { Send, User } from "iconoir-react"
import React, { useEffect, useMemo, useState } from "react"
import MessageInput from "../MessageInput"
import CallService from "@/lib/calls"

interface TranscriptProps {
  call: any
  messages: TranscriptMessage[]
  otherPartyName?: string
  showSendMessage?: boolean | undefined
  showSpeakerPill?: boolean | undefined
  onSendMessage?: (message: string) => Promise<void> | void | undefined
  customButtons?: React.ReactNode
  inCard?: boolean | undefined
}

const aiPill = (
  <span className="bg-linear-to-r from-purple-600 to-indigo-600 text-white text-lg font-medium rounded-full flex justify-center items-center h-10 w-10 shadow-md transition-all duration-200 hover:shadow-lg transform hover:scale-105">
    AI
  </span>
)

export default function Transcript({
  call,
  messages,
  otherPartyName,
  showSendMessage = true,
  showSpeakerPill = true,
  onSendMessage = undefined,
  customButtons = null,
  inCard = true
}: TranscriptProps): React.JSX.Element {
  const [message, setMessage] = useState("")
  const [containerHeight, setContainerHeight] = useState(0)
  const [callService, setCallService] = useState(() => new CallService(call))

  const groupedMessages = useMemo(() => {
    return messages.reduce((acc: TranscriptMessage[][], message) => {
      const lastGroup = acc[acc.length - 1]
      const lastMessage = lastGroup?.[0]

      if (lastMessage && lastMessage.speaker === message.speaker && !lastMessage.component && !message.component) {
        lastGroup.unshift(message)
      } else {
        acc.push([message])
      }
      return acc
    }, [])
  }, [messages])

  useEffect(() => {
    setCallService(new CallService(call))
  }, [call])

  const callFinished = ["completed", "errored"].includes(call.status)
  const callFinishedStr = callFinished ? "End of call" : null
  const sendMessageEnabled = call.status == "in_progress"

  const humanPill = useMemo(() => {
    return (
      <span className="bg-linear-to-r from-blue-700 to-blue-800 text-white text-lg font-medium rounded-full flex justify-center items-center h-10 w-10 shadow-md transition-all duration-200 hover:shadow-lg transform hover:scale-105">
        {otherPartyName ? <div>{otherPartyName.slice(0, 2).toUpperCase()}</div> : <User className="w-6 h-6" />}
      </span>
    )
  }, [otherPartyName])

  const handleSendMessage = () => {
    if (message.trim() !== "") {
      if (onSendMessage) {
        onSendMessage(message)
        setMessage("")
      }
    }
  }

  const sendButton = (
    <button
      className="flex h-[36px] w-[36px] text-white bg-linear-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
      onClick={handleSendMessage}
      disabled={!sendMessageEnabled}
    >
      <Send className="w-4 h-4" />
    </button>
  )

  const rightButtons = (
    <>
      {customButtons}
      {sendButton}
    </>
  )

  return (
    <div className={`flex flex-col w-full h-full ${inCard ? "bg-linear-to-b from-gray-50 to-white rounded" : ""}`}>
      <div className={`grow w-full h-[calc(100%-${containerHeight}px)] overflow-hidden`}>
        <div id="transcript" className="flex flex-col-reverse w-full h-full">
          <div className="flex flex-col-reverse w-full gap-6 py-6 overflow-auto px-4 sm:px-8">
            {groupedMessages.length >= 0 && callFinishedStr != null && (
              <div className="text-center font-medium text-gray-500 py-2 bg-gray-200 rounded-full shadow-sm">
                {callFinishedStr}
              </div>
            )}
            {groupedMessages.length == 0 && <div className="text-center font-medium text-gray-600">No messages.</div>}
            {groupedMessages.map((messages: any, index) => {
              if (messages[0].component) {
                return messages.map((message: any) => {
                  return (
                    <div
                      key={message.id}
                      className={`flex items-center leading-loose bg-gray-100  rounded-2xl px-6 py-4 shadow-sm transition-all duration-200 group-hover:shadow-md`}
                    >
                      <div className="flex flex-col items-center gap-3 max-w-4xl w-full">{message.component}</div>
                    </div>
                  )
                })
              }

              if (messages[0].speaker === "you") {
                const combinedText = messages
                  .map((m: any) => m.text)
                  .reverse()
                  .join("\n")
                return (
                  <div key={index} className="flex justify-center w-full my-2">
                    <div className="text-center text-base font-medium text-gray-500 bg-gray-100 rounded-xl px-4 py-2 max-w-lg shadow-sm">
                      <p className="whitespace-pre-wrap">{combinedText}</p>
                    </div>
                  </div>
                )
              }

              // Regular text messages with existing layout
              return (
                <div
                  key={index}
                  className={`flex gap-4 group ${messages[0].speaker === "ai" ? "flex-row" : "flex-row-reverse"}`}
                >
                  {showSpeakerPill && (
                    <div className="hidden sm:block min-w-fit">
                      <div className="w-14">{messages[0].speaker === "ai" ? aiPill : humanPill}</div>
                    </div>
                  )}
                  <div className={`flex flex-1 ${messages[0].speaker === "ai" ? "justify-start" : "justify-end"}`}>
                    <div className="flex flex-col gap-2 max-w-[90%] sm:max-w-[80%]">
                      {messages.map((message: any) => {
                        if (message.component) {
                          return (
                            <div
                              key={message.id}
                              className={`flex items-center leading-loose ${
                                messages[0].speaker === "ai" ? "bg-purple-50" : "bg-blue-50"
                              } rounded-2xl px-6 py-4 shadow-sm transition-all duration-200 group-hover:shadow-md`}
                            >
                              <div className="flex flex-col items-center gap-3 max-w-4xl w-full">
                                {message.component}
                              </div>
                            </div>
                          )
                        }
                        return (
                          <div
                            key={message.id}
                            className={`flex items-center leading-loose ${
                              messages[0].speaker === "ai" ? "bg-purple-50" : "bg-blue-50"
                            } rounded-2xl px-6 py-4 shadow-sm transition-all duration-200 group-hover:shadow-md`}
                          >
                            <p className="text-gray-700 whitespace-pre-wrap">{message.text}</p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
      {showSendMessage && !callService.completed() && (
        <div className="bg-transparent" id="transcript-message-input">
          <MessageInput
            message={message}
            placeholder="Send a private note or command to your AI. This is not spoken out loud."
            disabled={!sendMessageEnabled}
            onMessageChange={(e) => setMessage(e.target.value)}
            onHeightChange={setContainerHeight}
            rightButtons={rightButtons}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault()
                handleSendMessage()
              }
            }}
          />
        </div>
      )}
    </div>
  )
}
